
import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator';

// Utils
import { hasPermission } from '@/utils/Permissions';
import ErrorService from '@/utils/ErrorService';

// Api
import { createApartment, getAllApartments, updateApartment } from '@/utils/api/Apartments';

// Components
import VTable from '@/components/VTable.vue';
import VTableRow from '@/components/VTableRow.vue';
import VTableCell from '@/components/VTableCell.vue';
import VTableRowEmpty from '@/components/VTableRowEmpty.vue';
import VTableIconEdit from '@/components/VTableIconEdit.vue';
import VAdministrationSecondaryTable from '@/components/Administration/VAdministrationSecondaryTable.vue';

import VButton from '@/components/VButton/VButton.vue';
import VButtonIcon from '@/components/VButton/VButtonIcon.vue';
import VFormField from '@/components/VFormField.vue';
import VInput from '@/components/VInput.vue';

import VModalGeneral from '@/components/VModal/VModalGeneral.vue';

// Enums
import PermissionsEnum from '@/ship/Enums/PermissionsEnum';

// Interfaces
import Apartment from '@/ship/Models/Apartment';
import { TranslateResult } from 'vue-i18n';
type FilteredApartment = Pick<Apartment, 'id' | 'name'>;

@Component({
    components: {
        VTable,
        VTableRow,
        VTableCell,
        VTableRowEmpty,
        VTableIconEdit,

        VAdministrationSecondaryTable,

        VButtonIcon,
        VButton,
        VFormField,
        VInput,

        VModalGeneral,
    },
})
export default class VAdministrationApartmentsTable extends Vue {
    @Prop({ type: Number, required: true })
    public floorId!: number;

    @Ref() public modalConfigureApartment?: VModalGeneral;

    public apartments: FilteredApartment[] = [];
    public newApartmentName = '';
    public isModalLoading = false;
    public isTableLoading = false;
    public modalTitle: string | TranslateResult = '';
    public modalButtonLabel: string | TranslateResult = '';

    get hasEditAccess() {
        return hasPermission(PermissionsEnum.ApartmentsUpdate);
    }

    public modalActionHandler: () => void = () => ({});

    public createApartment() {
        this.modalTitle = this.$t('page.administration.apartmentCreating');
        this.modalButtonLabel = this.$t('create');
        this.modalActionHandler = this.createApartmentHandler;
        this.modalConfigureApartment?.showModal();
    }

    public updateApartment(row: FilteredApartment) {
        this.newApartmentName = row.name;
        this.modalTitle = this.$t('page.administration.apartmentEditing');
        this.modalButtonLabel = this.$t('update');
        this.modalActionHandler = () => this.updateApartmentHandler(row.id);
        this.modalConfigureApartment?.showModal();
    }

    @Emit('updated')
    public async createApartmentHandler() {
        this.isModalLoading = true;
        const response = await createApartment(this.newApartmentName, this.floorId).catch(ErrorService.handleApiError);
        if (response) await this.actualizeApartments();
        this.isModalLoading = false;
        this.modalConfigureApartment?.hideModal();
    }

    @Emit('updated')
    public async updateApartmentHandler(id: number) {
        this.isModalLoading = true;
        const response = await updateApartment(id, this.newApartmentName, this.floorId).catch(
            ErrorService.handleApiError,
        );
        if (response) await this.actualizeApartments();
        this.isModalLoading = false;
        this.modalConfigureApartment?.hideModal();
    }

    public async actualizeApartments() {
        const response = await getAllApartments<FilteredApartment>({
            orderBy: 'id',
            filter: 'id;name',
            search: `floor_id:${this.floorId}`,
            limit: 0,
        }).catch(ErrorService.handleApiError);

        if (response) this.apartments = response.data.data ?? [];
    }

    public resetNewApartmentFieldName() {
        this.newApartmentName = '';
    }

    public async created() {
        this.isTableLoading = true;
        await this.actualizeApartments();
        this.isTableLoading = false;
    }
}
